import { FC, Suspense, lazy } from 'react';
import { MetaType } from '@lmp/eduki-routes';
import ErrorBoundary from '../ui/error-boundary/error-boundary';
import { DocumentMetaProvider } from '../contexts/document-meta-context';
import { StoreType, injectReducer } from '../root-reducer';
import rootStore from '../store';

// --- Main pages (high-traffic) ---
const Home = lazy(() => import(/* webpackChunkName: "route-home" */ './home/home'));
const SearchResult = lazy(() => import(/* webpackChunkName: "route-search" */ './search-result/search-result'));
const MaterialPage = lazy(() => import(/* webpackChunkName: "route-material-page" */ './material-page/material-page'));
const Author = lazy(() => import(/* webpackChunkName: "route-author" */ './author/author'));
const Seller = lazy(() => import(/* webpackChunkName: "route-seller" */ './seller'));
const Favorites = lazy(() => import(/* webpackChunkName: "route-favorites" */ './favorites/favorites'));
const Recommendations = lazy(() => import(/* webpackChunkName: "route-recommendations" */ './recommendations/recommendations'));

// --- Landings ---
const Landing = lazy(() => import(/* webpackChunkName: "route-landing" */ './landing/landing'));
const NewLanding = lazy(() => import(/* webpackChunkName: "route-new-landing" */ './new-landing/new-landing'));
const KeywordLanding = lazy(() => import(/* webpackChunkName: "route-keyword-landing" */ './keyword-landing-page/keyword-landing-page'));
const SwitchLandingComponent = lazy(() => import(/* webpackChunkName: "route-switch-landing" */ './switch-landing-component/switch-component'));
const FreeMaterialsLanding = lazy(() => import(/* webpackChunkName: "route-free-materials" */ './free-materials-landing/free-materials-landing'));
const HomeSchooling = lazy(() => import(/* webpackChunkName: "route-home-schooling" */ './home-schooling/home-schooling'));
const ReferralsLanding = lazy(() => import(/* webpackChunkName: "route-referrals" */ './referrals-landing/referrals-landing'));
const SchoolCreate = lazy(() => import(/* webpackChunkName: "route-school-create" */ './school-create/school-create'));
const SchoolWishlist = lazy(() => import(/* webpackChunkName: "route-school-wishlist" */ './school-wishlist/school-wishlist'));

// --- Builder landings (static CMS pages, grouped into one chunk) ---
const Jobs = lazy(() => import(/* webpackChunkName: "builder-landings" */ './builder-landings/jobs/jobs'));
const LMPHistory = lazy(() => import(/* webpackChunkName: "builder-landings" */ './builder-landings/lmp-history/lmp-history'));
const schoolAccount = lazy(() => import(/* webpackChunkName: "builder-landings" */ './builder-landings/school-account/school-account'));
const Magazine = lazy(() => import(/* webpackChunkName: "builder-landings" */ './builder-landings/magazine/magazine'));
const ReferralsLandingForUnauthorized = lazy(
    () => import(/* webpackChunkName: "builder-landings" */ './builder-landings/referrals-landing-for-unauthorized/referrals-landing-for-unauthorized')
);
const MentalHealthLanding = lazy(() => import(/* webpackChunkName: "builder-landings" */ './builder-landings/mental-health-landing/mental-health-landing'));
const MentalHealthKidsLanding = lazy(
    () => import(/* webpackChunkName: "builder-landings" */ './builder-landings/mental-health-landing/mental-health-kids-landing')
);
const MentalHealthSurvey = lazy(() => import(/* webpackChunkName: "builder-landings" */ './builder-landings/mental-health-landing/mental-health-survey'));
const RussiaAttacksUkraineLanding = lazy(
    () => import(/* webpackChunkName: "builder-landings" */ './builder-landings/russia-attacks-ukraine/russia-attacks-ukraine')
);
const ClimateAction = lazy(() => import(/* webpackChunkName: "builder-landings" */ './builder-landings/climate-action/climate-action'));
const DonateUkraine = lazy(() => import(/* webpackChunkName: "builder-landings" */ './builder-landings/donate-ukraine/donate-ukraine'));
const Inclusion = lazy(() => import(/* webpackChunkName: "builder-landings" */ './builder-landings/inclusion/inclusion'));
const BuilderPreview = lazy(() => import(/* webpackChunkName: "builder-landings" */ './builder-landings/builder-preview/builder-preview'));
const InteractiveMainLanding = lazy(() => import(/* webpackChunkName: "builder-landings" */ './builder-landings/interactive-main/interactive-main'));
const Partners = lazy(() => import(/* webpackChunkName: "builder-landings" */ './builder-landings/partners/partners'));
const OurTeam = lazy(() => import(/* webpackChunkName: "builder-landings" */ './builder-landings/our-team/our-team'));
const MobileAppLanding = lazy(() => import(/* webpackChunkName: "builder-landings" */ './builder-landings/mobile-app-landing/mobile-app-landing'));
const AgroNordwest = lazy(() => import(/* webpackChunkName: "builder-landings" */ './builder-landings/agro-nordwest/agro-nordwest'));
const EndOfSchool2026 = lazy(() => import(/* webpackChunkName: "builder-landings" */ './builder-landings/end-of-school-2026/end-of-school-2026'));
const ImpactResearch = lazy(() => import(/* webpackChunkName: "builder-landings" */ './builder-landings/impact-research/impact-research'));
const AdaptLanding = lazy(() => import(/* webpackChunkName: "route-adapt-landing" */ './adapt-landing/adapt-landing'));
const QualityLanding = lazy(() => import(/* webpackChunkName: "builder-landings" */ './quality-landing/quality-landing'));

// --- Cart and payment ---
const Cart = lazy(() => import(/* webpackChunkName: "route-cart", webpackPrefetch: true */ './cart/cart'));
const Payment = lazy(() => import(/* webpackChunkName: "route-payment" */ './payment/payment'));
const ThankYouPage = lazy(() => import(/* webpackChunkName: "route-thank-you" */ './thank-you-page/thank-you-page'));
const ThankYouPageInvoice = lazy(() => import(/* webpackChunkName: "route-thank-you" */ './thank-you-page/thank-you-page-invoice'));

// --- Authentication ---
const SignIn = lazy(() => import(/* webpackChunkName: "route-sign-in" */ './sign-in/sign-in'));
const SignUp = lazy(() => import(/* webpackChunkName: "route-sign-up" */ './sign-up/sign-up'));
const SignUpConfirm = lazy(() => import(/* webpackChunkName: "auth" */ './sign-up-confirm/sign-up-confirm'));
const ChangeEmailConfirm = lazy(() => import(/* webpackChunkName: "auth" */ './change-email-confirm/change-email-confirm'));
const ForgotPassword = lazy(() => import(/* webpackChunkName: "auth" */ './forgot-password/forgot-password'));
const PasswordRecovery = lazy(() => import(/* webpackChunkName: "auth" */ './password-recovery/password-recovery'));
const FinishRegistration = lazy(() => import(/* webpackChunkName: "auth" */ './finish-registration/finish-registration'));

// --- Cabinet: upload wizard ---
const Wizard = lazy(() => import(/* webpackChunkName: "cabinet-wizard" */ './cabinet/material-wizard/material-wizard'));

// --- Cabinet: settings (grouped because they are small) ---
const InvoiceInfo = lazy(() => import(/* webpackChunkName: "cabinet-settings" */ './cabinet/settings/invoice-info/invoice-info'));
const ChangePassword = lazy(() => import(/* webpackChunkName: "cabinet-settings" */ './cabinet/settings/change-password/change-password'));
const ChangeEmail = lazy(() => import(/* webpackChunkName: "cabinet-settings" */ './cabinet/settings/change-email/change-email'));
const ChangeCountry = lazy(() => import(/* webpackChunkName: "cabinet-settings" */ './cabinet/settings/change-country/change-country'));
const BankDetails = lazy(() => import(/* webpackChunkName: "cabinet-settings" */ './cabinet/settings/bank-details/bank-details'));
const PreferenceCenter = lazy(() => import(/* webpackChunkName: "cabinet-settings" */ './cabinet/preference-center/preference-center'));
const Fonts = lazy(() => import(/* webpackChunkName: "cabinet-settings" */ './cabinet/tools/fonts'));

// --- Cabinet: sales ---
const MyUploads = lazy(() => import(/* webpackChunkName: "cabinet-sales" */ './cabinet/sales/my-uploads/my-uploads'));
const CreditNotes = lazy(() => import(/* webpackChunkName: "cabinet-sales" */ './cabinet/sales/credit-notes/credit-notes'));
const Credits = lazy(() => import(/* webpackChunkName: "cabinet-sales" */ './cabinet/sales/credits/credits'));
const Vouchers = lazy(() => import(/* webpackChunkName: "cabinet-sales" */ './cabinet/sales/vouchers/vouchers'));
const MySales = lazy(() => import(/* webpackChunkName: "cabinet-sales" */ './cabinet/sales/my-sales/my-sales'));
const Charts = lazy(() => import(/* webpackChunkName: "cabinet-sales" */ './cabinet/sales/charts/charts'));
const Metrics = lazy(() => import(/* webpackChunkName: "cabinet-sales" */ './cabinet/sales/metrics/metrics'));

// --- Cabinet: purchased materials ---
const DownloadsList = lazy(() => import(/* webpackChunkName: "cabinet-downloads" */ './cabinet/bought-materials/downloads-list/downloads-list'));
const InvoicesAndTaxes = lazy(() => import(/* webpackChunkName: "cabinet-downloads" */ './cabinet/bought-materials/invoices-and-taxes/invoices-and-taxes'));

// --- Material: additional pages ---
const MaterialFeedback = lazy(() => import(/* webpackChunkName: "route-material-feedback" */ './material-feedback/material-feedback'));
const MaterialCardPreview = lazy(() => import(/* webpackChunkName: "route-material-preview" */ './material-card-preview/material-card-preview'));
const ImpactPage = lazy(() => import(/* webpackChunkName: "route-impact" */ './impact-page/impact-page'));
const FreeDownload = lazy(() => import(/* webpackChunkName: "route-free-download" */ './free-download/free-download'));
const QualityReport = lazy(() => import(/* webpackChunkName: "route-quality-report" */ './quality-report/quality-report'));

// --- Content ---
const Blogpost = lazy(() => import(/* webpackChunkName: "route-blogpost" */ './blogpost/blogpost'));
const CreateBlogpost = lazy(() => import(/* webpackChunkName: "route-create-blogpost" */ './create-blogpost/create-blogpost'));
const HelpCenter = lazy(() => import(/* webpackChunkName: "route-help-center" */ './help-center/help-center'));
const Podcasts = lazy(() => import(/* webpackChunkName: "route-podcasts" */ './podcasts/podcasts'));
const MySchool = lazy(() => import(/* webpackChunkName: "route-my-school" */ './my-school/my-school'));
const MySubjects = lazy(() => import(/* webpackChunkName: "route-my-subjects" */ './my-subjects/my-subjects'));
const SellerSteps = lazy(() => import(/* webpackChunkName: "route-seller-steps" */ './seller-steps/seller-steps'));
const Nps = lazy(() => import(/* webpackChunkName: "route-nps" */ './nps/nps'));

// --- Campaigns ---
const BackToSchool2026Landing = lazy(
    () => import(/* webpackChunkName: "campaign-back-to-school-2025" */ './back-to-school-campaign-2026/pages/landing/back-to-school-2026-landing')
);
const BackToSchool2026TC = lazy(() => import(/* webpackChunkName: "campaign-back-to-school-2025" */ './back-to-school-campaign-2026/pages/tc/back-to-school-2026-tc'));
const PromoActivation = lazy(() => import(/* webpackChunkName: "route-promo-activation" */ './promo-activation/promo-activation'));
const EngagementProgramFaq = lazy(() => import(/* webpackChunkName: "route-engagement-faq" */ './engagement-program-faq/engagement-program-faq'));

// --- AI tools ---
const AiTools = lazy(() => import(/* webpackChunkName: "route-ai-tools" */ './ai-tools/pages/ai-tools/ai-tools'));
const AiToolPage = lazy(() => import(/* webpackChunkName: "route-ai-tool-page" */ './ai-tools/pages/ai-tool-page/ai-tool-page'));
const AiToolDocument = lazy(() => import(/* webpackChunkName: "route-ai-tool-document" */ './ai-tools/pages/ai-tool-document/ai-tool-document'));
const AiToolsMyDocuments = lazy(() => import(/* webpackChunkName: "route-ai-tool-documents" */ './ai-tools/pages/ai-tool-my-documents/ai-tools-my-documents'));
const AiToolsFaq = lazy(() => import(/* webpackChunkName: "route-ai-tool-faq" */ './ai-tools/pages/ai-tool-faq/ai-tools-faq'));

// --- Interactive editor ---
const NewInteractive = lazy(() => import(/* webpackChunkName: "route-interactive" */ './new-interactive/interactive'));

// --- Info pages (legal, small) ---
const Contact = lazy(() => import(/* webpackChunkName: "info-pages" */ './info/contact/contact'));
const Impressum = lazy(() => import(/* webpackChunkName: "info-pages" */ './info/impressum/impressum'));
const Agb = lazy(() => import(/* webpackChunkName: "info-pages" */ './info/agb/agb'));
const GdprJobs = lazy(() => import(/* webpackChunkName: "info-pages" */ './info/gdpr-jobs/gdpr-jobs'));
const OrderCancellation = lazy(() => import(/* webpackChunkName: "route-order-cancellation" */ './order-cancellation/order-cancellation'));

// --- Utilities and redirects ---
const AutoLogin = lazy(() => import(/* webpackChunkName: "utils-redirects" */ './auto-login/auto-login'));
const AuthorRedirect = lazy(() => import(/* webpackChunkName: "utils-redirects" */ './author-redirect/author-redirect'));
const MicrosoftRedirectURI = lazy(() => import(/* webpackChunkName: "utils-redirects" */ './microsoft-redirect-uri/microsoft-redirect-uri'));
const AbandonedCartRedirect = lazy(() => import(/* webpackChunkName: "utils-redirects" */ './abandoned-cart-redirect/abandoned-cart-redirect'));
const LoginAs = lazy(() => import(/* webpackChunkName: "utils-redirects" */ './login-as/login-as'));
const Unsubscribe = lazy(() => import(/* webpackChunkName: "utils-redirects" */ './unsubscribe/unsubscribe'));
const UnsubscribeAuthor = lazy(() => import(/* webpackChunkName: "utils-redirects" */ './unsubscribe-author/unsubscribe-author'));
const MailingUnsubscribe = lazy(() => import(/* webpackChunkName: "utils-redirects" */ './mailing-unsubscribe/mailing-unsubscribe'));
const Api = lazy(() => import(/* webpackChunkName: "utils-redirects" */ './api/api'));
const NotFound = lazy(() => import(/* webpackChunkName: "utils-redirects" */ '../common/not-found/not-found'));

const Components = {
    AbandonedCartRedirect,
    Metrics,
    Agb,
    AiTools,
    AiToolPage,
    AiToolDocument,
    AiToolsMyDocuments,
    AiToolsFaq,
    GdprJobs,
    AgroNordwest,
    Api,
    Author,
    AuthorRedirect,
    AutoLogin,
    BankDetails,
    Blogpost,
    BuilderPreview,
    Cart,
    ChangeCountry,
    ChangeEmail,
    ChangeEmailConfirm,
    ChangePassword,
    Charts,
    ClimateAction,
    Contact,
    CreateBlogpost,
    CreditNotes,
    Credits,
    DonateUkraine,
    DownloadsList,
    Favorites,
    FinishRegistration,
    Fonts,
    ForgotPassword,
    FreeDownload,
    FreeMaterialsLanding,
    HelpCenter,
    Home,
    HomeSchooling,
    Impressum,
    Inclusion,
    InteractiveMainLanding,
    InvoiceInfo,
    InvoicesAndTaxes,
    NewInteractive,
    Jobs,
    KeywordLanding,
    LMPHistory,
    Landing,
    LoginAs,
    Magazine,
    MailingUnsubscribe,
    MaterialFeedback,
    MaterialCardPreview,
    ImpactPage,
    MentalHealthKidsLanding,
    MentalHealthLanding,
    MentalHealthSurvey,
    MicrosoftRedirectURI,
    MobileAppLanding,
    MySales,
    MySchool,
    MySubjects,
    MyUploads,
    NewLanding,
    MaterialPage,
    NotFound,
    Nps,
    OrderCancellation,
    OurTeam,
    Partners,
    PasswordRecovery,
    Payment,
    Podcasts,
    PreferenceCenter,
    Recommendations,
    ReferralsLanding,
    ReferralsLandingForUnauthorized,
    RussiaAttacksUkraineLanding,
    SchoolCreate,
    SchoolWishlist,
    SearchResult,
    Seller,
    SellerSteps,
    SignIn,
    SignUp,
    SignUpConfirm,
    SwitchLandingComponent,
    ThankYouPage,
    ThankYouPageInvoice,
    Unsubscribe,
    UnsubscribeAuthor,
    Vouchers,
    Wizard,
    schoolAccount,
    BackToSchool2026Landing,
    BackToSchool2026TC,
    EndOfSchool2026,
    ImpactResearch,
    PromoActivation,
    QualityLanding,
    QualityReport,
    EngagementProgramFaq,
    AdaptLanding
};

type LoadedReducersType = {
    key: string;
    path: string;
};

type AsyncComponentPropsType = (
    | { loadedReducers?: LoadedReducersType[] }
    | { [key: string]: boolean | string | number }
) & {
    meta: () => MetaType;
    componentName: string;
};

const AsyncComponent: FC<AsyncComponentPropsType> = (props) => {
    const { componentName, loadedReducers } = props;

    if (Array.isArray(loadedReducers)) {
        loadedReducers.forEach(({ path, key }) => {
            // eslint-disable-next-line no-unsanitized/method
            import(`./${path}/reducers`).then(({ default: reducer }) => {
                injectReducer(rootStore as unknown as StoreType, { key, reducer });
            });
        });
    }

    const Component = Components[componentName];

    return (
        <Suspense fallback={<div />}>
            <ErrorBoundary withText>
                <Suspense fallback={<div />}>
                    <DocumentMetaProvider meta={props.meta}>
                        <Component {...props} />
                    </DocumentMetaProvider>
                </Suspense>
            </ErrorBoundary>
        </Suspense>
    );
};

export default AsyncComponent;
